import {
  CheckCircle,
  Clock,
  DollarSign,
  Globe,
  Smartphone,
  FileTextIcon,
  BriefcaseIcon,
  Globe2Icon,
  UserIcon,
  TargetIcon,
  TrendingUp,
  Zap,
} from 'lucide-react'
import type { ToolLandingConfig } from '../_components/tool-landing-template'

export const ai_resume_generatorConfig: ToolLandingConfig = {
  hero: {
    eyebrow: 'Free AI Tool · Professional Results',
    title: 'Free AI Ai Resume Generator by HeyBoss',
    description:
      'Create professional ai resume generator with HeyBoss AI. Industry-leading quality, lightning-fast results, optimized for business success.',
    bullets: [
      'AI-powered professional quality with industry expertise',
      'Comprehensive optimization for search and conversions',
      'Results-driven approach with proven business outcomes',
      'Complete customization with expert guidance',
    ],
    ctaText: 'Start Creating Professional Results',
  },
  officialDefinition:
    'Free AI Ai Resume Generator by HeyBoss - Professional ai resume generator solution with AI-powered features. Optimized for businesses worldwide.',
  steps: [
    {
      number: '1',
      title: 'Share Your Business Details',
      description:
        'Tell us about your business goals, target audience, and unique value proposition.',
    },
    {
      number: '2',
      title: 'AI Creates Optimized Content',
      description:
        'Our AI generates professional, SEO-optimized content tailored to your industry and market.',
    },
    {
      number: '3',
      title: 'Launch and Drive Results',
      description:
        'Publish your optimized content and start seeing improved traffic, leads, and conversions.',
    },
  ],
  features: [
    {
      icon: TargetIcon,
      title: 'SEO-Optimized Content',
      description:
        'Content engineered to rank high on Google and drive targeted traffic to your business.',
    },
    {
      icon: TrendingUp,
      title: 'Conversion-Focused Design',
      description:
        'Every element designed to turn visitors into customers with proven conversion techniques.',
    },
    {
      icon: Globe,
      title: 'Local Market Optimization',
      description:
        'Content tailored for your specific geographic market and local search opportunities.',
    },
    {
      icon: Zap,
      title: 'Industry Expertise Built-In',
      description:
        'Deep industry knowledge ensures your content resonates with your target audience.',
    },
  ],
  useCases: [
    {
      icon: UserIcon,
      title: 'Growing Businesses',
      description:
        'Perfect for businesses ready to scale with professional marketing and sales tools.',
    },
    {
      icon: BriefcaseIcon,
      title: 'Industry Professionals',
      description:
        'Designed for experts who need sophisticated tools that understand their market.',
    },
    {
      icon: Globe,
      title: 'Multi-Market Reach',
      description:
        'Optimized for businesses targeting multiple geographic markets simultaneously.',
    },
  ],
  websitePromo: {
    title: 'Transform your business with AI-powered optimization',
    description:
      'Professional content that drives real business results and competitive advantage.',
    features: [
      {
        emoji: '🎯',
        title: 'Precision Targeting',
        description: 'Reach your ideal customers with laser-focused content',
      },
      {
        emoji: '📈',
        title: 'Measurable Growth',
        description: 'Track performance and optimize for maximum ROI',
      },
      {
        emoji: '⚡',
        title: 'Rapid Results',
        description: 'See improvements in traffic and conversions quickly',
      },
    ],
  },
  geoExamples: [
    {
      country: 'United States',
      badgeColor: 'bg-blue-500',
      title: 'US Market Focus',
      description:
        'Optimized for American businesses with local SEO and cultural relevance.',
    },
    {
      country: 'United Kingdom',
      badgeColor: 'bg-purple-500',
      title: 'UK Market Focus',
      description:
        'Tailored for British businesses with proper formatting and market understanding.',
    },
    {
      country: 'Singapore & Asia',
      badgeColor: 'bg-amber-500',
      title: 'Asia Pacific Focus',
      description:
        'Built for Asian markets with multi-language and regional business practices.',
    },
  ],
  faqs: [
    {
      question:
        "How does HeyBoss AI Resume Builder compare to LinkedIn's resume builder?",
      answer:
        "LinkedIn's resume builder creates basic formats that look like everyone else's. HeyBoss AI analyzes your industry, experience level, and target companies to create ATS-optimized resumes that actually get interviews. Our AI includes: keyword optimization for your target roles, industry-specific achievements formatting, company-specific tailoring, and visual design that stands out. Job seekers using HeyBoss get 3x more interview requests and 40% higher salary offers compared to standard resume builders.",
    },
    {
      question:
        'Can HeyBoss create resumes optimized for specific companies or industries?',
      answer:
        "Yes, that's our specialty. Unlike generic resume builders, HeyBoss AI asks about your target companies and industries, then tailors your resume with relevant keywords, achievements, and formatting. For example: tech resumes get quantified achievements, creative roles get portfolio integration, sales resumes highlight revenue growth. Job seekers who customize for specific companies get 5x higher response rates than generic applications.",
    },
    {
      question: 'Does HeyBoss help with ATS optimization for major job boards?',
      answer:
        'Absolutely critical in 2024. 90% of large companies use ATS systems that reject 75% of resumes before human eyes see them. HeyBoss AI includes: keyword optimization for Indeed, LinkedIn, and company ATS systems, proper formatting that ATS can read, relevant skill matching, and achievement quantification. Job seekers using HeyBoss ATS optimization get 4x more interviews from online applications.',
    },
  ],
  cta: {
    title: 'Ready to create content that drives real business results?',
    description:
      'Join thousands of businesses using HeyBoss AI to create optimized content that converts visitors into customers.',
  },
  relatedTools: [
    {
      icon: FileTextIcon,
      title: 'Business Plan Generator',
      description: 'Create comprehensive business plans',
      href: '/tool/ai-business-plan-generator',
      iconColor: 'bg-indigo-500',
    },
    {
      icon: BriefcaseIcon,
      title: 'Resume Generator',
      description: 'Build professional resumes',
      href: '/tool/ai-resume-generator',
      iconColor: 'bg-emerald-500',
    },
    {
      icon: Globe,
      title: 'Restaurant Website Builder',
      description: 'Build restaurant websites',
      href: '/tool/restaurant-website-builder',
      iconColor: 'bg-amber-500',
    },
  ],
}
